import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import Swal from "sweetalert2";

const Contact = () => {
  const [result, setResult] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();

    // Step 1: Show confirmation dialog
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to submit the form?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, submit it!",
      cancelButtonText: "No, cancel!",
    });

    if (!confirmation.isConfirmed) return;

    // Step 2: Show progress dialog
    Swal.fire({
      title: "Sending...",
      text: "Please wait while your message is being sent.",
      icon: "info",
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const formData = new FormData(event.target);

      // Add the access key for web3forms (ensure this is set in your .env file)
      formData.append("access_key", process.env.REACT_APP_WEBFORM_SECRET_KEY);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      Swal.close(); // Close the loading spinner

      if (data.success) {
        Swal.fire({
          title: "Success!",
          text: "Your message has been sent successfully.",
          icon: "success",
          confirmButtonText: "Great!",
        });
        setResult("Form Submitted Successfully");
        event.target.reset();
      } else {
        Swal.fire({
          title: "Error!",
          text: data.message || "Something went wrong. Please try again.",
          icon: "error",
          confirmButtonText: "Try Again",
        });
        setResult("Submission Failed");
      }
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: "Error!",
        text: "Network error. Please check your connection.",
        icon: "error",
        confirmButtonText: "Try Again",
      });
      console.error("Error submitting form:", error);
      setResult("Submission Failed");
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative bg-cover bg-center h-[60vh] flex items-center justify-center"
          style={{
            backgroundImage: "url('/images/valueblinds-1.jpg')",
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
          <h1 className="relative text-5xl font-bold text-white drop-shadow-lg text-center">
            Get in Touch with Us
          </h1>
        </motion.div>

        {/* Contact Info Section */}
        <div className="container mx-auto py-12 px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8 text-center"
          >
            {[
              { icon: <FaPhoneAlt />, title: "Call Us", info: "+1 (234) 567-890" },
              { icon: <FaEnvelope />, title: "Email Us", info: "info@valueblinds.com" },
              { icon: <FaMapMarkerAlt />, title: "Visit Us", info: "123 Blinds Street, City, Country" },
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-white shadow-xl p-6 rounded-lg transition duration-300 transform"
              >
                <div className="flex items-center justify-center text-4xl text-[#01A9DE] mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className="text-gray-600 mt-2">{item.info}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Contact Form */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
          className="bg-white py-12 px-8 shadow-2xl max-w-2xl mx-auto rounded-lg overflow-hidden relative"
        >
          {/* Decorative Elements */}
          <div className="absolute -top-5 -left-5 w-20 h-20 bg-[#01A9DE] opacity-20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-5 -right-5 w-20 h-20 bg-[#0274BD] opacity-20 rounded-full blur-3xl"></div>

          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-3xl font-semibold text-center mb-6 text-gray-800"
          >
            Send Us a Message
          </motion.h2>

          <form onSubmit={onSubmit} className="space-y-6">
            <motion.input
              whileFocus={{ scale: 1.02, boxShadow: "0px 0px 10px rgba(1, 169, 222, 0.3)" }}
              type="text"
              name="name" // Required for FormData
              placeholder="Your Name"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#01A9DE] transition bg-gray-100"
              required
            />
            <motion.input
              whileFocus={{ scale: 1.02, boxShadow: "0px 0px 10px rgba(1, 169, 222, 0.3)" }}
              type="email"
              name="email" // Required for FormData
              placeholder="Your Email"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#01A9DE] transition bg-gray-100"
              required
            />
            <motion.textarea
              whileFocus={{ scale: 1.02, boxShadow: "0px 0px 10px rgba(1, 169, 222, 0.3)" }}
              name="message" // Required for FormData
              placeholder="Your Message"
              rows="5"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#01A9DE] transition bg-gray-100"
              required
            />
            <motion.button
              whileHover={{ scale: 1.05, backgroundColor: "#0274BD" }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="w-full bg-[#01A9DE] text-white py-3 rounded-lg text-lg font-semibold transition shadow-lg"
            >
              Send Message
            </motion.button>
          </form>
          {result && (
            <p className={`text-center mt-4 ${result.includes("Success") ? "text-green-600" : "text-red-600"}`}>
              {result}
            </p>
          )}
        </motion.div>

        {/* Google Map Embed */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="mt-12"
        >
          <iframe
            title="Google Map"
            className="w-full h-80 shadow-lg"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.8354345093745!2d144.9537363153153!3d-37.81627974202144!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad642af0f11fd81%3A0xf5770bce38b92f99!2sBlinds%20Store!5e0!3m2!1sen!2sus!4v1622628761732!5m2!1sen!2sus"
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.div>
      </div>
      <Footer />
    </>
  );
};

export default Contact;